<template>
  <rest-page ref="page" :fetch-endpoint="fetchEndpoint" layout="tabbed">
    <page-header-new
      :title="title"
      :tag="status"
      :breadcrumbs="breadcrumbs"
      :avatar="avatar"
    >
      <template slot="actions">
        <profile-actions-dropdown
          :profile-id="profileId"
          :project-id="projectId"
          :in-seed-list="model.in_seed_list"
          @refreshModel="refreshModel"
        />
      </template>
    </page-header-new>

    <page-tabs :tabs="tabs">
      <template slot="router">
        <router-view
          :readonly="readonly"
          :profile-id="profileId"
          :profile-num="model.num"
          :project-id="projectId"
          @email-updated="emailUpdated"
          @attribute-updated="attributeUpdated"
        />
      </template>
    </page-tabs>
  </rest-page>
</template>

<script>
import RestPage from '@/common/components/rest/RestPage'
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import PageTabs from '@/common/components/page/PageTabs'
import ProfileActionsDropdown from '@/modules/profiles/components/ProfileActionsDropdown'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import { $hasFeature } from '@/core/utils/VueAcl'
import iframeTitleMixin from '@/common/mixins/iframeTitleMixin'
import crypto from 'crypto'

export default {
  name: 'ProfilePage',

  mixins: [iframeTitleMixin],

  components: {
    RestPage,
    PageHeaderNew,
    PageTabs,
    ProfileActionsDropdown
  },

  data() {
    return {
      model: {}
    }
  },

  mounted() {
    this.$store.dispatch('reloadProjectConfig')
  },

  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `user-profiles/${this.profileId}`,
        handler: data => {
          this.model = data
        }
      })
    },

    profileId() {
      return this.$route.query.profile_id
    },

    projectId() {
      return this.$route.query.project_id
    },

    readonly() {
      return !this.$can('Profile::Update')
    },

    breadcrumbs() {
      return [
        {
          label: this.$t('Users_Breadcrumb_Data')
        },
        {
          label: this.$t('Users_Breadcrumb_Profiles'),
          route: projectRoute({
            name: 'Profiles'
          })
        },
        {
          label: this.profileId
        }
      ]
    },

    tabs() {
      return [
        {
          title: this.$t('Profiles_Tab_Overview'),
          route: projectRoute({
            name: 'ProfileOverview',
            query: {
              profile_id: this.profileId,
              project_id: this.projectId
            }
          }),
          show: true
        },
        {
          title: this.$t('Profiles_Tab_Data'),
          route: projectRoute({
            name: 'ProfileData',
            query: {
              profile_id: this.profileId,
              project_id: this.projectId
            }
          }),
          show: true
        },
        {
          title: this.$t('Profiles_Tab_CategorisedAttributes'),
          route: projectRoute({
            name: 'ProfileCategorisedAttributes',
            query: {
              profile_id: this.profileId,
              project_id: this.projectId,
              profile_num: this.model.num
            }
          }),
          show:
            $hasFeature('tagCategories') &&
            this.$can('CategorisedTagFeatured::Read')
        },
        {
          title: this.channelsTitle,
          route: projectRoute({
            name: 'ProfileChannels',
            query: {
              profile_id: this.profileId,
              project_id: this.projectId,
              profile_num: this.model.num
            }
          }),
          show: true
        },
        {
          title: this.$t('Profiles_Tab_Devices'),
          route: projectRoute({
            name: 'ProfileDevices',
            query: {
              profile_id: this.profileId,
              project_id: this.projectId
            }
          }),
          show:
            !this.$isRole('consent-customer-service-agent') &&
            this.$can('Device::Read')
        },
        {
          title: this.$t('Profiles_Tab_Messages'),
          route: projectRoute({
            name: 'ProfileMessages',
            query: {
              profile_id: this.$route.query.profile_id,
              project_id: this.$route.query.project_id
            }
          }),
          show: true
        },
        {
          title: this.$t('Profiles_Tab_Tags'),
          route: projectRoute({
            name: 'ProfileTags',
            query: {
              profile_id: this.$route.query.profile_id,
              project_id: this.$route.query.project_id,
              profile_num: this.model.num
            }
          }),
          show: this.$can('viewProjectItem')
        },
        {
          title: this.$t('Profiles_Tab_Sessions'),
          route: projectRoute({
            name: 'ProfileSessions',
            query: {
              profile_id: this.$route.query.profile_id,
              project_id: this.$route.query.project_id,
              profile_num: this.model.num
            }
          }),
          show:
            this.$isRole('admin') ||
            this.$isRole('admin-viewer') ||
            this.$isRole('admin-support')
        },
        {
          title: this.$t('Profiles_Tab_Events'),
          route: projectRoute({
            name: 'ProfileEvents',
            query: {
              profile_id: this.$route.query.profile_id,
              project_id: this.$route.query.project_id
            }
          }),
          show: this.$can('viewProjectItem')
        },
        {
          title: this.$t('Profiles_Tab_Purchases'),
          route: projectRoute({
            name: 'ProfilePurchases',
            query: {
              profile_id: this.$route.query.profile_id,
              project_id: this.$route.query.project_id
            }
          }),
          show: this.hasPurchaseEvent && this.$can('viewProjectItem')
        },
        {
          title: this.$t('Profiles_Tab_Locations'),
          route: projectRoute({
            name: 'ProfileLocations',
            query: {
              profile_id: this.profileId,
              project_id: this.projectId
            }
          }),
          show: this.hasMobileApps && this.$can('viewProjectItem')
        },
        {
          title: this.$t('Profiles_Tab_AuditLog'),
          route: projectRoute({
            name: 'ProfileAuditLog',
            query: {
              profile_id: this.profileId,
              project_id: this.projectId,
              profile_num: this.model.num
            }
          }),
          show: this.hasAuditLog
        },
        {
          title: this.$t('Profiles_Tab_MergeHistory'),
          route: projectRoute({
            name: 'ProfileMergeHistory',
            query: {
              profile_id: this.profileId,
              project_id: this.projectId,
              profile_num: this.model.num
            }
          }),
          show: true
        },
        {
          title: this.$t('Profiles_Tab_VoucherCodes'),
          route: projectRoute({
            name: 'ProfileVoucherCodes',
            query: {
              profile_id: this.profileId,
              project_id: this.projectId
            }
          }),
          show: this.hasVoucherCodes && this.$can('viewProjectItem')
        }
      ].filter(tab => tab.show === true)
    },

    title() {
      if (!this.$can('UserPersonalData::Read')) {
        return this.$t('Profiles_Title_Details')
      } else if (this.model.name !== null && this.model.name !== '') {
        return this.model.name
      } else if (this.model.email !== null && this.model.email !== '') {
        return this.model.email
      } else if (this.model.user_id !== null && this.model.user_id !== '') {
        return `${this.$t('Profiles_Field_User')} ${this.model.user_id}`
      } else {
        return this.$t('Profiles_Field_AnonymousUser')
      }
    },

    avatar() {
      if (!this.hasGravatar) {
        return '/img/avatar.jpg'
      }

      if (this.model.email) {
        const img = [
          '//www.gravatar.com/avatar/',
          this.emailHash,
          `?d=mp`,
          `&r=g`
        ]

        return img.join('')
      } else {
        return '//www.gravatar.com/avatar?&d=retro&r=g'
      }
    },

    emailHash() {
      return crypto
        .createHash('sha256')
        .update(this.model.email.trim().toLowerCase(), 'utf8')
        .digest('hex')
    },

    hasGravatar() {
      return this.$store.state.app.provider.gravatar_on
    },

    hasMobileApps() {
      return this.$store.state.app.features.mobile === true
    },

    hasPurchaseEvent() {
      return this.$store.state.app.features.purchaseEvent === true
    },

    hasAuditLog() {
      return (
        this.$store.state.app.features.auditLogProfile === true &&
        this.$can('AuditLog:ReadAccess')
      )
    },

    hasVoucherCodes() {
      return $hasFeature('allowVoucherCodes')
    },

    status() {
      if (this.model.merge_to !== null) {
        return {
          type: 'primary',
          label: this.$t('UserProfiles_Field_Archived')
        }
      }
    },

    channelsTitle() {
      return $hasFeature('subscriptionPreferences')
        ? this.$t('Profiles_Tab_ChannelsAndPreferences')
        : this.$t('Profiles_Tab_Channels')
    }
  },

  methods: {
    emailUpdated(newEmail) {
      this.model.email = newEmail
    },

    attributeUpdated(attributeModel) {
      if (
        attributeModel.name === 'first_name' ||
        attributeModel.name === 'last_name'
      ) {
        this.refreshModel()
      }
    },

    refreshModel() {
      RestApi(this.fetchEndpoint).then(({ data }) => {
        Object.assign(this.model, data)
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/profiles/pages/ProfilePage.vue